import {
  capitalize,
  Checkbox,
  FormControlLabel,
  Paper,
  Typography,
} from "@mui/material";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { StepProps } from "./StepProps";
import { TractorDimensionsSubform } from "portal/views/equipment/tractors/TractorDimensionsSubform";
import { TractorViewModel } from "../TractorViewModel";
import { typedPath } from "typed-path";
import { useFormikContext } from "formik";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { FC } from "react";

const S = typedPath<TractorViewModel>();

export const DriveInfoStep: FC<StepProps> = ({ disabled }) => {
  const { measurementSystem } = useSelf();
  const { t } = useTranslation();
  const formikProps = useFormikContext<TractorViewModel>();
  const isDisabled = disabled || !formikProps.values.driveInfo.override;

  return (
    <div className="flex flex-col gap-2">
      <Paper className="flex flex-col gap-2 p-4">
        <div className="flex gap-1">
          <InfoOutlinedIcon />
          <Typography>
            {capitalize(t("views.autotractor.equipment.customInfoBlurb"))}
          </Typography>
        </div>
        <FormControlLabel
          label={t("views.autotractor.equipment.tractors.hasCustomDriveInfo")}
          checked={formikProps.values.driveInfo.override}
          control={<Checkbox disabled={disabled} />}
          onChange={() => {
            formikProps.setFieldValue(
              S.driveInfo.override.$path,
              !formikProps.values.driveInfo.override
            );
          }}
        />
      </Paper>
      <Paper className="flex flex-col gap-2 p-4">
        <Typography className="font-bold">
          {capitalize(t("views.autotractor.equipment.tractors.driveInfo"))}
        </Typography>
        <div className="flex flex-wrap p-4 gap-4">
          <div className="flex gap-2">
            <FormikDimensionedNumberInput
              name={S.driveInfo.minTurningRadiusM.$path}
              label={capitalize(t("models.equipment.turningRadius"))}
              tProps={{ minimum: 0 }}
              dimensionedNumberInputProps={{
                minStoredValue: 0,
                storedUnit: "m",
                localSystem: measurementSystem,
                displayUnits: {
                  imperial: "ft",
                  metric: "m",
                },
                disabled: isDisabled,
              }}
            />
          </div>
          <div className="flex flex-col w-full">
            <Typography>
              {capitalize(t("models.equipment.steeringRange"))}
            </Typography>
            <div className="flex px-2 gap-2">
              <FormikDimensionedNumberInput
                label={capitalize(t("utils.descriptors.minimumShort"))}
                name={S.driveInfo.steeringRange.min.$path}
                tooltipText={t("models.equipment.steeringRangeTooltip")}
                dimensionedNumberInputProps={{
                  storedUnit: "deg",
                  localSystem: measurementSystem,
                  displayUnits: {
                    metric: "deg",
                    imperial: "deg",
                  },
                  disabled: isDisabled,
                }}
              />
              <FormikDimensionedNumberInput
                label={capitalize(t("utils.descriptors.maximumShort"))}
                name={S.driveInfo.steeringRange.max.$path}
                tooltipText={t("models.equipment.steeringRangeTooltip")}
                dimensionedNumberInputProps={{
                  storedUnit: "deg",
                  localSystem: measurementSystem,
                  displayUnits: {
                    metric: "deg",
                    imperial: "deg",
                  },
                  disabled: isDisabled,
                }}
              />
            </div>
          </div>
        </div>
      </Paper>
      <TractorDimensionsSubform
        basePath={S.driveInfo}
        mode={
          formikProps.values.driveInfo.override && !disabled
            ? "tractor"
            : "readonly"
        }
      />
    </div>
  );
};
